// FLAGS: identity, personal-data, silent-failure
// Registers the axios interceptors every OpenUp.Api call passes through: request headers, App Insights error
// telemetry, 401/403 refresh-then-logout, and the translated error message. No credential is attached here —
// both session kinds are cookies, carried by `withCredentials` (OI-25561).
import type {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import axios, { AxiosHeaders } from "axios";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { getApiBaseUrl } from "@src/utils/urlHelpers";
import { apiPaths } from "@src/utils/constants";
import authService from "@src/services/auth.service";
import { logout as logoutAction } from "@src/actions/userActions";
import store from "@src/store";
import { appInsights } from "@src/appInsights/AppInsights";
import { redactPii } from "@src/utils/piiRedactor";
import { buildApiRequestHeaders } from "@src/utils/requestHeaders";
import {
  hasPhaseTwoSession,
  refreshPhaseTwoSession,
} from "@src/services/phaseTwoSession";
import { isProduction } from "@src/env";
import { HttpClient as HttpClientV1 } from "@src/api/v1/http-client";
import { HttpClient as HttpClientV2 } from "@src/api/v2/http-client";
import i18n from "@src/i18n";
import {
  OpenUpHttpClient,
  OpenUpHttpClientV2,
} from "@src/api/OpenUpHttpClient";

interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  wasRetried?: boolean;
}

const STATUS_CODES = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  CONFLICT: 409,
  REQUEST_TOO_LONG: 414,
  INTERNAL_SERVER_ERROR: 500,
} as const;

const ERROR_MESSAGE_MAP: Record<number, string> = {
  [STATUS_CODES.INTERNAL_SERVER_ERROR]: "ErrorStrings.InternalServerError",
  [STATUS_CODES.UNAUTHORIZED]: "ErrorStrings.Unauthorized",
  [STATUS_CODES.FORBIDDEN]: "ErrorStrings.Forbidden",
  [STATUS_CODES.REQUEST_TOO_LONG]: "ErrorStrings.RequestToLong",
  [STATUS_CODES.CONFLICT]: "ErrorStrings.Conflict",
};

const AUTHORIZATION_ERROR_STATUS_CODES = new Set<number>([
  STATUS_CODES.FORBIDDEN,
  STATUS_CODES.UNAUTHORIZED,
]);

let refreshTokenPromise: Promise<void> | null = null;
let interceptorsRegistered = false;

export const translateCell = (cell: string): string => i18n.t(cell);

const requestHeaderInterceptor = async (
  config: InternalAxiosRequestConfig,
): Promise<InternalAxiosRequestConfig> => {
  const headers = config.headers ?? new AxiosHeaders();

  // No Authorization header for a Phase Two session (OI-25561): its credential is an
  // HttpOnly cookie, which `withCredentials` sends on its own. The shared headers still
  // matter — `User-Agent-Operating-System` is what the API requires before it will read
  // that cookie, which is how a cross-site request is refused.
  Object.entries(buildApiRequestHeaders()).forEach(([name, value]) =>
    headers.set(name, value),
  );

  config.headers = headers;
  return config;
};

const getResponseDataString = (data: unknown, url?: string): string => {
  if (data === null || data === undefined) {
    return "";
  }
  try {
    if (typeof data === "string") {
      return data.substring(0, 500);
    }
    return JSON.stringify(data).substring(0, 500);
  } catch {
    return `Could not stringify response data for ${url ?? "unknown URL"}`;
  }
};

// FLAGS: personal-data
// Only the URL is passed through redactPii; the response body reaches App Insights as-is, so a failing
// health-data endpoint puts up to 500 characters of its body in telemetry.
const loggingInterceptor = (error: AxiosError): never => {
  if (axios.isCancel(error)) {
    throw error;
  }

  const status = error.response?.status;
  const url = error.config?.url ?? "Unknown URL";

  const trackingError = new Error(
    `HTTP ${status ?? "Network/CORS"}: ${redactPii(url)}`,
  );
  trackingError.stack = error.stack;

  const trackingData = {
    exception: trackingError,
    severityLevel:
      status && status >= 500 ? SeverityLevel.Error : SeverityLevel.Warning,
    properties: {
      statusCode: String(status ?? "N/A"),
      url: redactPii(url),
      method: error.config?.method?.toUpperCase() ?? "UNKNOWN",
      responseData: getResponseDataString(error.response?.data, url),
      errorMessage: error.message || "No message",
    },
  };

  try {
    appInsights.trackException(trackingData);
  } catch (appInsightsError) {
    if (!isProduction()) {
      // eslint-disable-next-line no-console
      console.warn(
        "Failed to track exception to Application Insights:",
        appInsightsError,
      );
    }
  }

  throw error;
};

const handleLogout = (): void => {
  store.dispatch(logoutAction());
};

// The feature-flag read runs in the boot sequence, before sign-in and before React mounts; a 401 there must
// reach the caller (which fails closed) rather than drive a refresh and sign the visitor out.
const isSpecialRequest = (url: string | undefined): boolean => {
  if (!url) return false;
  return (
    url.startsWith(apiPaths.login) ||
    url.startsWith(apiPaths.twoFactorAuthentication) ||
    url.startsWith(apiPaths.registerUser) ||
    url.startsWith(apiPaths.featureFlags)
  );
};

const isRefreshRelatedRequest = (url: string | undefined): boolean => {
  if (!url) return false;
  return url.startsWith(apiPaths.refreshToken);
};

const hasRecaptchaToken = (config: ExtendedAxiosRequestConfig): boolean => {
  try {
    const data =
      typeof config.data === "string" ? JSON.parse(config.data) : config.data;
    return data?.recaptchaToken !== undefined;
  } catch {
    return false;
  }
};

// Renews the session and nothing else: the retry is the caller's, so a failure here always means the
// renewal itself was refused.
const handleTokenRefresh = async (): Promise<void> => {
  // Both session kinds refresh here, and both are single-flighted by the shared promise
  // below. For Phase Two (OI-25561) that is not a nicety: the realm rotates the refresh
  // token on every use, so a second concurrent refresh would present a token the realm has
  // already spent. This is what keeps one tab's burst of 401s to one refresh call. Two
  // separate tabs — or the two-factor client, which sits outside this stack — can still
  // collide, and what catches that is the realm's refreshTokenMaxReuse, 2 on all four
  // realms, the equivalent of the API's RefreshTokenLeewaySeconds.
  const refresh = hasPhaseTwoSession()
    ? (): Promise<void> => refreshPhaseTwoSession()
    : (): Promise<void> => authService.refreshAccessToken();

  refreshTokenPromise ??= refresh().finally(() => {
    setTimeout(() => {
      refreshTokenPromise = null;
    }, 500);
  });

  await refreshTokenPromise;
};

// FLAGS: identity
// Every early return below is a logout-or-not decision; wasRetried caps the retry at one, so a widened guard or a
// lost wasRetried flag turns a 401 into either a refresh loop or a session dropped without warning.
const refreshTokenInterceptor = async (
  error: AxiosError,
): Promise<AxiosResponse> => {
  const originalRequest = error.config as
    | ExtendedAxiosRequestConfig
    | undefined;
  const url = originalRequest?.url;
  const status = error.response?.status;
  const isAuthError =
    status !== undefined && AUTHORIZATION_ERROR_STATUS_CODES.has(status);

  if (!isAuthError) throw error;

  if (isSpecialRequest(url)) throw error;

  if (originalRequest && hasRecaptchaToken(originalRequest)) throw error;

  if (url === apiPaths.logout) throw error;

  if (originalRequest?.wasRetried) {
    handleLogout();
    throw error;
  }

  if (isRefreshRelatedRequest(url) || !originalRequest) {
    handleLogout();
    throw error;
  }

  if (!authService.getShouldRetryLoginFromStorage()) {
    handleLogout();
    throw error;
  }

  originalRequest.wasRetried = true;
  try {
    await handleTokenRefresh();
  } catch (refreshError) {
    handleLogout();
    throw refreshError;
  }

  // Outside the try on purpose (OI-26200): the session is renewed by now, so whatever the retry answers is
  // the page's to handle. A 404 such as "no completed session yet" used to land in the catch above and sign
  // the member out. A retry still refused for auth comes back through this interceptor with wasRetried set,
  // and that early return is what signs out.
  return axios(originalRequest);
};

const errorMessageInterceptor = (error: AxiosError): never => {
  const status = error.response?.status;
  let message: string =
    (error.request as XMLHttpRequest)?.responseText ||
    error.message ||
    error.toString();

  if (!error.response && error.message === "Network Error") {
    message = navigator.onLine
      ? translateCell("ErrorStrings.NetworkError")
      : translateCell("ErrorStrings.OfflineError");
  } else if (error.response && status && ERROR_MESSAGE_MAP[status]) {
    message = translateCell(ERROR_MESSAGE_MAP[status]);
  }

  error.message = message;
  throw error;
};

const getAxiosInstance = (
  client: typeof axios | HttpClientV1<unknown> | HttpClientV2<unknown>,
): AxiosInstance | null => {
  if ("interceptors" in client) return client;
  if (client.instance) return client.instance;
  return null;
};

// FLAGS: silent-failure
// Runs once, over the three clients listed inside it. Any axios instance built later — OpenUpHttpClientWithBaseURL
// is the live case — gets no headers, no telemetry and no 401 retry, and nothing reports the omission.
export const registerInterceptors = (): void => {
  if (interceptorsRegistered) {
    return;
  }

  axios.defaults.withCredentials = true;
  axios.defaults.baseURL = getApiBaseUrl();

  const clients = [axios, OpenUpHttpClient, OpenUpHttpClientV2];
  const passThrough = (res: AxiosResponse): AxiosResponse => res;

  clients.forEach((client) => {
    const instance = getAxiosInstance(client);
    if (!instance) return;

    instance.interceptors.request.use(requestHeaderInterceptor);

    // Response interceptors (order matters: log → refresh → message)
    instance.interceptors.response.use(passThrough, loggingInterceptor);
    instance.interceptors.response.use(passThrough, refreshTokenInterceptor);
    instance.interceptors.response.use(passThrough, errorMessageInterceptor);
  });

  interceptorsRegistered = true;
};
